import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, usePermissions } from '@hooks';
import Drawer from '@components/Drawer';
import { LocationIntegrationName } from '@constants/integrations';
import { isIntegrationConnected } from '@helpers/integrations';
import { RoutePath } from '@constants/routes';
import {
    alertActions,
    coverManagerActions,
    accountActions,
    facebookActions,
    googleActions,
    vtexActions,
    locationsProfilesActions,
} from '@actions';
import { connectLocationIntegration } from '@services';
import { handleApiError } from '@helpers/api';
import { objectIsEmpty } from '@helpers/objectIsEmpty';
import LocationCard from './LocationCard';
import GoogleLocationPicker from './GoogleLocationPicker';

const GenericIntegration = ({
    onClose,
    onConnect,
    isOpen,
    selectedCompany,
    setSelectedCompany,
    integrationType,
    profileType,
}) => {
    const { t } = useTranslation();
    const [location, setLocation] = useState({});

    const account = useSelector(state => state.account.account);
    const { hasFullSettings } = usePermissions();

    const canManageIntegrations = hasFullSettings();

    const data = {
        [LocationIntegrationName.CoverManager]: useSelector(state => state.coverManager.data),
        [LocationIntegrationName.Facebook]: useSelector(state => state.facebook.data),
        [LocationIntegrationName.Google]: useSelector(state => state.google.data),
        [LocationIntegrationName.VTEX]: useSelector(state => state.vtex.data),
    };
    const LOCATIONS = data[integrationType];
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const dataFetchMachine = {
        [LocationIntegrationName.CoverManager]: coverManagerActions.get,
        [LocationIntegrationName.Facebook]: facebookActions.getPages,
        [LocationIntegrationName.Google]: googleActions.get,
        [LocationIntegrationName.VTEX]: vtexActions.get,
    };

    const dataResetMachine = {
        [LocationIntegrationName.CoverManager]: coverManagerActions.reset,
        [LocationIntegrationName.Facebook]: facebookActions.reset,
        [LocationIntegrationName.Google]: googleActions.reset,
        [LocationIntegrationName.VTEX]: vtexActions.reset,
    };

    useEffect(() => {
        if (!LOCATIONS.length) {
            dispatch(dataFetchMachine[integrationType]());
        }
    }, [LOCATIONS, dispatch]);

    useEffect(() => {
        if (LOCATIONS?.length) {
            const availableLocation = LOCATIONS.find(l => !l.connected);
            if (availableLocation) {
                setLocation(availableLocation);
            }
        }
    }, [LOCATIONS]);

    // Any connected account of this type enables linking (an account may have several).
    const INTEGRATION_CONNECTED = isIntegrationConnected(account, profileType);

    const integrationIdProp = {
        [LocationIntegrationName.CoverManager]: 'slug',
        [LocationIntegrationName.Facebook]: 'id',
        [LocationIntegrationName.Google]: 'locationId',
        [LocationIntegrationName.VTEX]: 'accountName',
    };

    const goToIntegrations = () => navigate(RoutePath.AccountSettingsIntegrations);

    const onConnectIntegration = async () => {
        try {
            onClose();
            // Pass the selected resource's owner id (multi-account types carry it; single-account types don't).
            const updatedCompany = await connectLocationIntegration(
                integrationType,
                selectedCompany.companyId,
                location,
                location?.integrationId,
            );
            await dispatch(accountActions.get());
            await dispatch(locationsProfilesActions.get(selectedCompany.companyId));
            dispatch(dataResetMachine[integrationType]());
            setSelectedCompany(updatedCompany);
            dispatch(alertActions.success(t('SettingsCompanies.connectIntegrationMsg')));
        } catch (error) {
            handleApiError(error);
        } finally {
            onConnect();
        }
    };

    const getPrimaryActionProps = () => {
        if (!INTEGRATION_CONNECTED && canManageIntegrations) {
            return {
                primaryAction: goToIntegrations,
                primaryText: t('SettingsCompanies.goToIntegrations'),
                primaryDisabled: false,
            };
        } else if (INTEGRATION_CONNECTED && canManageIntegrations) {
            return {
                primaryAction: onConnectIntegration,
                primaryText: t('buttons.connect'),
                primaryDisabled: objectIsEmpty(location),
            };
        } else if (!INTEGRATION_CONNECTED && !canManageIntegrations) {
            return {
                primaryAction: goToIntegrations,
                primaryText: t('SettingsCompanies.goToIntegrations'),
                primaryDisabled: true,
            };
        }
    };

    return (
        <Drawer
            aboveModal
            anchor="right"
            open={isOpen}
            onClose={onClose}
            title={
                INTEGRATION_CONNECTED
                    ? t('SettingsCompanies.connectLocation')
                    : t('SettingsCompanies.connectDrawerNotConnected')
            }
            subtitle={
                INTEGRATION_CONNECTED
                    ? t('SettingsCompanies.connectDrawerDescription')
                    : t('SettingsCompanies.connectDrawerNotConnectedDescription')
            }
            {...getPrimaryActionProps()}
        >
            {INTEGRATION_CONNECTED ? (
                integrationType === LocationIntegrationName.Google ? (
                    <GoogleLocationPicker locations={LOCATIONS} selectedLocation={location} setLocation={setLocation} />
                ) : (
                    <div style={{ display: 'grid', gridGap: '8px' }}>
                        {LOCATIONS.map((item, index) => (
                            <LocationCard
                                key={index}
                                location={item}
                                selectedLocation={location}
                                setLocation={setLocation}
                                integrationType={integrationType}
                                integrationIdProp={integrationIdProp[integrationType]}
                                title={integrationType === LocationIntegrationName.VTEX ? item.accountName : item.name}
                            />
                        ))}
                    </div>
                )
            ) : null}
        </Drawer>
    );
};

export default GenericIntegration;
